import { type ReactNode } from "react";
import { ServiceTitanIconLink } from "@/core/components/service-titan-icon-link";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { ServiceTitanSymbol } from "@/accounts-payable/common";
import { cn } from "@/core/utils/utils";
import { sentenceCase } from "./utils";

/** The grey inline card an action's ServiceTitan record (or edit form) sits in. */
export const RECORD_CARD_CLASS = "rounded-md bg-bg-secondary px-3 py-3";

/**
 * A ServiceTitan object link — the Invoices table / sidebar link in
 * interactive-text (green-70, 6.8:1 on white), not the core ST links'
 * primary (green-50, 3.3:1, below AA at 13px).
 */
export const StLink = ({
  url,
  label,
  children,
}: {
  url: string;
  label: string;
  children: ReactNode;
}) => (
  <ServiceTitanIconLink
    url={url}
    label={label}
    className="ml-0 text-md text-interactive-text hover:text-interactive-text hover:underline"
  >
    {children}
  </ServiceTitanIconLink>
);

/**
 * The ServiceTitan record an action touches, as a grey inline card: the object
 * type as a label, the record itself under it (with the ServiceTitan symbol
 * only when it links into ServiceTitan), then its fields.
 */
export const StRecord = ({
  label,
  isNew,
  link,
  name,
  children,
}: {
  label: string;
  /** True when the agent is creating this record in ServiceTitan rather than referencing an existing one. */
  isNew?: boolean;
  /** The ServiceTitan object link — shown with the ServiceTitan symbol. */
  link?: ReactNode;
  /** The record's name when there's no link to show. */
  name?: ReactNode;
  children?: ReactNode;
}) => (
  <Stack gap="md" fullWidth className={RECORD_CARD_CLASS}>
    <RecordField label={sentenceCase(isNew ? `New ${label}` : label)}>
      {link ? (
        <Stack direction="row" gap="xs" align="center" className="flex-wrap">
          <ServiceTitanSymbol className="size-4 flex-none text-primary-text" />
          {link}
        </Stack>
      ) : (
        name
      )}
    </RecordField>
    {children}
  </Stack>
);

/**
 * A label / value pair inside a record card: core `InfoWithLabel`'s label
 * (13px semibold tertiary) over a 13px value, so values never outrank the
 * action's own 13px title.
 */
export const RecordField = ({
  label,
  fullWidth,
  className,
  children,
}: {
  label: string;
  fullWidth?: boolean;
  className?: string;
  children: ReactNode;
}) => (
  <Stack gap="xs" fullWidth={fullWidth} className={cn("min-w-0", className)}>
    <Typography variant="subtitleW3" textColor="tertiary">
      {label}
    </Typography>
    {typeof children === "string" ? (
      <Typography variant="bodyW2" className="break-words">
        {children}
      </Typography>
    ) : (
      children
    )}
  </Stack>
);

/** Short fields side by side; three columns so they line up across cards. */
export const RecordFieldGrid = ({ children }: { children: ReactNode }) => (
  <Stack gap="none" fullWidth className="grid grid-cols-3 gap-x-4 gap-y-3">
    {children}
  </Stack>
);

/** Card for actions that touch no ServiceTitan object. */
export const NoStCard = ({ reason }: { reason: string }) => (
  <Stack gap="none" fullWidth className={RECORD_CARD_CLASS}>
    <RecordField label="No ServiceTitan change">{reason}</RecordField>
  </Stack>
);
